import { useState, useEffect } from 'react';
import { Box, Typography, Button, useTheme, useMediaQuery } from '@mui/material';
import { ExitToApp as ExitIcon, AccessTime as ClockIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const DemoBanner = () => {
  const { isDemoMode, demoSession, requestDemoLogout } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isWarning, setIsWarning] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!isDemoMode || !demoSession) {
      return;
    }

    const calculateTimeRemaining = () => {
      const now = new Date();
      const expiresAt = new Date(demoSession.expiresAt);
      const diffMs = expiresAt - now;

      if (diffMs <= 0) {
        // Session expired - show feedback modal
        requestDemoLogout();
        return null;
      }

      const totalSeconds = Math.floor(diffMs / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      // Warning state if less than 5 minutes remaining
      setIsWarning(totalSeconds < 300);

      return { minutes, seconds };
    };

    // Initial calculation
    const time = calculateTimeRemaining();
    setTimeRemaining(time);

    // Update every second
    const interval = setInterval(() => {
      const time = calculateTimeRemaining();
      setTimeRemaining(time);
    }, 1000);

    return () => clearInterval(interval);
  }, [isDemoMode, demoSession, requestDemoLogout]);

  if (!isDemoMode || !timeRemaining) {
    return null;
  }

  const formatTime = () => {
    const { minutes, seconds } = timeRemaining;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const backgroundColor = isWarning ? '#FFD700' : 'rgb(212, 82, 33)';
  const textColor = isWarning ? '#021c2d' : '#ffffff';

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: { xs: 'auto', md: '48px' },
        backgroundColor,
        color: textColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: { xs: 1, md: 0 },
        gap: { xs: 1, md: 2 },
        zIndex: 1300,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        transition: 'background-color 0.3s ease, color 0.3s ease',
        flexWrap: { xs: 'wrap', md: 'nowrap' }
      }}
    >
      {/* DEMO MODE Label */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: { xs: '1.1rem', md: '1.3rem' },
          fontWeight: 'normal',
          letterSpacing: '0.05em'
        }}
      >
        <Typography
          component="span"
          sx={{
            fontFamily: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit'
          }}
        >
          DEMO MODE
        </Typography>
      </Box>

      {/* Separator */}
      {!isMobile && (
        <Box sx={{ width: '1px', height: '24px', backgroundColor: textColor, opacity: 0.3 }} />
      )}

      {/* Timer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: { xs: '0.875rem', md: '0.95rem' }
        }}
      >
        <ClockIcon sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }} />
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            fontWeight: 500
          }}
        >
          {formatTime()}
        </Typography>
      </Box>

      {/* Separator */}
      {!isMobile && (
        <Box sx={{ width: '1px', height: '24px', backgroundColor: textColor, opacity: 0.3 }} />
      )}

      {/* Email */}
      <Typography
        sx={{
          fontSize: { xs: '0.75rem', md: '0.875rem' },
          fontWeight: 400,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: { xs: '150px', md: 'none' }
        }}
      >
        {demoSession.prospectEmail}
      </Typography>

      {/* Separator */}
      {!isMobile && (
        <Box sx={{ width: '1px', height: '24px', backgroundColor: textColor, opacity: 0.3 }} />
      )}

      {/* Exit Button */}
      <Button
        startIcon={<ExitIcon />}
        onClick={requestDemoLogout}
        size="small"
        sx={{
          color: textColor,
          textTransform: 'none',
          fontSize: { xs: '0.75rem', md: '0.875rem' },
          fontWeight: 500,
          border: `1px solid ${textColor}`,
          borderRadius: 1,
          px: { xs: 1.5, md: 2 },
          py: { xs: 0.5, md: 0.75 },
          minWidth: 'auto',
          '&:hover': {
            backgroundColor: isWarning ? 'rgba(2, 28, 45, 0.1)' : 'rgba(255, 255, 255, 0.2)',
            borderColor: textColor
          }
        }}
      >
        Exit Demo
      </Button>
    </Box>
  );
};

export default DemoBanner;
